import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, LogIn, KeyRound, Clock } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [hardLockout, setHardLockout] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          setHardLockout(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user) return <Navigate to={location.state?.from?.pathname || "/carts"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (cooldown > 0) return;
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Giriş başarısız");
      if (res.wait_seconds && res.wait_seconds > 0) {
        setCooldown(res.wait_seconds);
        setHardLockout(!!res.hard_lockout);
      }
      return;
    }
    navigate(location.state?.from?.pathname || "/carts", { replace: true });
  };

  const disabled = submitting || cooldown > 0;
  const cooldownLabel = (() => {
    if (cooldown <= 0) return "";
    if (cooldown < 60) return `${cooldown} sn`;
    const m = Math.floor(cooldown / 60);
    const s = cooldown % 60;
    return `${m} dk ${String(s).padStart(2, "0")} sn`;
  })();

  return (
    <div className="min-h-screen w-full flex items-stretch">
      {/* Left informational panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-zinc-950 text-white px-14 py-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-400">
            Dahili Panel · noindex
          </div>
          <div className="mt-2 text-4xl font-black tracking-tighter leading-none">
            OE WMS BY G
            <br />
            Sepet Yöneticisi
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="border border-zinc-800 px-2 py-2">
            <div className="text-white text-2xl font-black">01</div>
            <div className="mt-2">Sepetleri Çek</div>
          </div>
          <div className="border border-zinc-800 px-2 py-2">
            <div className="text-white text-2xl font-black">02</div>
            <div className="mt-2">Seç</div>
          </div>
          <div className="border border-zinc-800 px-2 py-2">
            <div className="text-white text-2xl font-black">03</div>
            <div className="mt-2">Taslak Üret</div>
          </div>
        </div>

        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
            <ShieldCheck className="h-3 w-3 text-[#002FA7]" />
            Güvenli Oturum
          </div>
          <h2 className="mt-2 text-3xl font-black tracking-tighter">Giriş yap</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Yalnızca yetkili operatörler.
          </p>

          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="email"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-600"
              >
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-zinc-300 focus-visible:ring-[#002FA7]"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-600"
              >
                Parola
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm border-zinc-300 focus-visible:ring-[#002FA7]"
                required
                data-testid="login-password-input"
              />
            </div>

            {error ? (
              <div
                className={`text-xs px-3 py-2 rounded-sm border ${
                  cooldown > 0
                    ? (hardLockout
                        ? "text-red-800 bg-red-50 border-red-300"
                        : "text-amber-800 bg-amber-50 border-amber-300")
                    : "text-red-600 bg-red-50 border-red-200"
                }`}
                data-testid="login-error"
              >
                <div>{error}</div>
                {cooldown > 0 ? (
                  <div
                    className="mt-1 flex items-center gap-1 font-mono text-[11px]"
                    data-testid="login-cooldown"
                  >
                    <Clock className="h-3 w-3" />
                    Kalan: {cooldownLabel}
                  </div>
                ) : null}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={disabled}
              className="w-full rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white h-10 text-sm font-semibold transition-colors duration-150 disabled:opacity-70"
              data-testid="login-submit-btn"
            >
              {cooldown > 0 ? (
                <span className="font-mono text-xs uppercase tracking-widest">
                  Bekleyin · {cooldownLabel}
                </span>
              ) : submitting ? (
                <span className="font-mono text-xs uppercase tracking-widest">
                  Bağlanılıyor…
                </span>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Oturumu başlat
                </>
              )}
            </Button>

            <div className="pt-4 text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
              <KeyRound className="h-3 w-3" />
              Hesap oluşturma yalnızca yönetici üzerinden yapılır.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
